/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { MessageFormProps, MessageFormField } from '../types';
import { DestinationCheckboxFields } from './DestinationCheckboxFields';
import { MessageField } from './MessageField';
import { QuestionsFilter } from './QuestionsFilter';
import { SubjectField } from './SubjectField';
import { UserSelectField } from './UserSelectField';
import { getFilterOptions } from './QuestionsOptions';
import { QuestionStatusStats, UserDelegationStats } from '../../../types/initiativeStats';

interface BulkUsersMessageFormProps extends MessageFormProps {
  users: UserDelegationStats[];
}

const getFilteredUsers = (users: UserDelegationStats[], value: QuestionStatusStats) => {
  if (value === 'all') {
    return users;
  }

  // delegationStats is decided based on toggle,
  // to simplify logic we always check totalDelegation, as it not clear
  // that toggle will affect the result in separate modal.
  switch (value) {
    case 'created':
      return users.filter(({ totalDelegation }) => totalDelegation?.created);
    case 'updated':
      return users.filter(({ totalDelegation }) => totalDelegation?.updated);
    case 'rejected':
      return users.filter(({ totalDelegation }) => totalDelegation?.contributorRejected || totalDelegation?.verifierRejected);
    case 'verified':
      return users.filter(({ totalDelegation }) => totalDelegation?.verified);
  }
}

export const BulkUsersMessageForm = (props: BulkUsersMessageFormProps) => {
  const { onChange, form, users } = props;
  const [usersState, setUsers] = useState<UserDelegationStats[]>(users);

  const onChangeQuestionsFilter = (value: QuestionStatusStats) => {
    const filteredUsers = getFilteredUsers(users, value);
    const filteredIds = filteredUsers.map(user => user._id);
    setUsers(filteredUsers);
    onChange(MessageFormField.UserIds)(filteredIds);
  };

  return (
    <div className='d-flex flex-column'>
      <div className='row'>
        <div className='col-7'>
          <QuestionsFilter label='Message users with' options={getFilterOptions} onChange={onChangeQuestionsFilter} />
        </div>
      </div>
      <UserSelectField users={usersState} userIds={form.userIds} onChange={onChange(MessageFormField.UserIds)} />
      <SubjectField subject={form.subject} onChange={onChange(MessageFormField.Subject)} />
      <MessageField message={form.message} onChange={onChange(MessageFormField.Message)} />
      <DestinationCheckboxFields sendByEmail={form.sendByEmail} onChange={onChange(MessageFormField.SendByEmail)} />
    </div>
  );
}
